import React, { useState, useEffect } from "react";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Clock,
  CheckCircle,
  AlertCircle,
  FileText,
} from "lucide-react";
import api from "../api/client";

const STATUS_COLORS = {
  reviewing: "bg-amber-400",
  published: "bg-green-400",
  failed: "bg-red-400",
  publishing: "bg-blue-400",
  queued: "bg-gray-400",
  generating: "bg-blue-400",
};

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year, month) {
  return new Date(year, month, 1).getDay();
}

export default function ContentCalendar({ selectedSite }) {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfWeek(year, month);
  const monthName = currentDate.toLocaleString("default", { month: "long" });

  useEffect(() => {
    if (!selectedSite) return;
    setLoading(true);
    api
      .get(`/websites/${selectedSite.id}/pages/calendar/`)
      .then((res) => setPages(res.data || []))
      .catch(() => setPages([]))
      .finally(() => setLoading(false));
  }, [selectedSite, month, year]);

  const getPagesByDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return pages.filter((p) => {
      const schedDate = p.schedule_date ? p.schedule_date.slice(0, 10) : null;
      const pubDate = p.published_at ? p.published_at.slice(0, 10) : null;
      return schedDate === dateStr || pubDate === dateStr;
    });
  };

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  if (!selectedSite)
    return <div className="text-gray-500 text-center py-20">Select a website</div>;

  const dayPages = selectedDay ? getPagesByDay(selectedDay) : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <CalendarIcon size={20} className="text-copper" /> Content Calendar
        </h2>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3 text-xs">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" /> Scheduled</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400" /> Published</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400" /> Failed</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-400" /> In Progress</span>
          </div>
        </div>
      </div>

      {/* Month navigation */}
      <div className="flex items-center justify-between bg-dark-300 border border-dark-50 rounded-xl p-4">
        <button onClick={prevMonth} className="p-2 hover:bg-dark-50/50 rounded-lg">
          <ChevronLeft size={18} className="text-gray-400" />
        </button>
        <h3 className="text-lg font-semibold">
          {monthName} {year}
        </h3>
        <button onClick={nextMonth} className="p-2 hover:bg-dark-50/50 rounded-lg">
          <ChevronRight size={18} className="text-gray-400" />
        </button>
      </div>

      {loading ? (
        <div className="animate-pulse h-96 bg-dark-300 rounded-xl" />
      ) : (
        <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
          {/* Day headers */}
          <div className="grid grid-cols-7 border-b border-dark-50">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <div key={d} className="px-2 py-2 text-center text-xs font-medium text-gray-500">
                {d}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7">
            {/* Empty cells before first day */}
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`empty-${i}`} className="min-h-[100px] border-b border-r border-dark-50/50 bg-dark/20" />
            ))}

            {/* Day cells */}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dayData = getPagesByDay(day);
              const isToday =
                day === new Date().getDate() &&
                month === new Date().getMonth() &&
                year === new Date().getFullYear();
              const isSelected = selectedDay === day;

              return (
                <div
                  key={day}
                  onClick={() => setSelectedDay(day === selectedDay ? null : day)}
                  className={`min-h-[100px] border-b border-r border-dark-50/50 p-1.5 cursor-pointer hover:bg-dark-50/30 transition-colors ${
                    isSelected ? "bg-copper/10 ring-1 ring-copper" : ""
                  }`}
                >
                  <div className={`text-xs font-mono mb-1 ${isToday ? "text-copper font-bold" : "text-gray-500"}`}>
                    {day}
                  </div>
                  <div className="space-y-0.5">
                    {dayData.slice(0, 3).map((p) => (
                      <div
                        key={p.id}
                        className="flex items-center gap-1 text-[10px] truncate"
                      >
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${STATUS_COLORS[p.status] || "bg-gray-400"}`} />
                        <span className="text-gray-300 truncate">{p.title}</span>
                      </div>
                    ))}
                    {dayData.length > 3 && (
                      <div className="text-[10px] text-gray-500">+{dayData.length - 3} more</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Selected day detail */}
      {selectedDay && dayPages.length > 0 && (
        <div className="bg-dark-300 border border-dark-50 rounded-xl p-5 space-y-3">
          <h3 className="text-sm font-semibold text-gray-300">
            {monthName} {selectedDay}, {year} — {dayPages.length} page(s)
          </h3>
          {dayPages.map((p) => (
            <div key={p.id} className="flex items-center justify-between bg-dark/40 rounded-lg p-3">
              <div className="flex items-center gap-3">
                <FileText size={14} className="text-copper" />
                <div>
                  <div className="text-sm text-gray-200">{p.title}</div>
                  <div className="text-xs text-gray-500">
                    {p.target_keyword}
                    {p.target_location && ` · ${p.target_location}`}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {p.seo_score && (
                  <span className="text-xs font-mono text-gray-400">Score: {p.seo_score}</span>
                )}
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                  p.status === "published"
                    ? "bg-green-900/30 text-green-400"
                    : p.status === "reviewing"
                    ? "bg-amber-900/30 text-amber-400"
                    : p.status === "failed"
                    ? "bg-red-900/30 text-red-400"
                    : "bg-blue-900/30 text-blue-400"
                }`}>
                  {p.status === "published" && <CheckCircle size={10} />}
                  {p.status === "reviewing" && <Clock size={10} />}
                  {p.status === "failed" && <AlertCircle size={10} />}
                  {p.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
